import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate, defineMessages, injectIntl } from 'react-intl';
import invoke from 'lodash/invoke';
import { Link } from 'react-router';

import * as constants from '../Authorization/constants';

import Button from '../../components/Button';
import Header from '../../components/Header';
import Modal from '../../components/Modal';
import FairDetails from '../../components/FairDetails';
import HighSchoolDetailDisplay from '../HighSchoolDetailsDisplay';
import { getFairAvailabilityAction } from '../../utils/fair-utils';

import './styles.css';
import { onHEAccountSubscription } from '../../utils/subscriptions';

const messages = defineMessages({
  fairRegistrationDeadline: {
    id: 'fair.registration.deadline',
    defaultMessage: 'Registration deadline',
  },
});

const freemiumOrHubsSubscriptionsPredicate = (heAccount) => !heAccount.freemium &&
  heAccount.activeSubscriptions.some((s) => s !== constants.HUBS_SUBSCRIPTION);

const FreemiumOrHubsSubscriptionFairName = onHEAccountSubscription(
  freemiumOrHubsSubscriptionsPredicate,
  { failure: ({ title }) => <span styleName="header-fair">{title}</span> }, /* eslint-disable-line react/prop-types */
)(({ title }) => <span styleName="header-light">{title}</span>);

export const FreemiumOrHubsSubscriptionSchoolName = onHEAccountSubscription(
  freemiumOrHubsSubscriptionsPredicate,
  { failure: ({ title }) => <Header as="h3" styleName="header-strong" color="black">{title}</Header> }, /* eslint-disable-line react/prop-types */
)(({ title, onClick }) => <Link onClick={onClick}><Header as="h3" styleName="header-strong" color="teal">{title}</Header></Link>); /* eslint-disable-line jsx-a11y/anchor-is-valid */

class FairAvailability extends React.PureComponent {
  static propTypes = {
    schoolId: PropTypes.string.isRequired,
    schoolName: PropTypes.string.isRequired,
    schoolPhone: PropTypes.string,
    primaryContact: PropTypes.shape({
      mail: PropTypes.string,
    }).isRequired,
    schoolAddress: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      zipCode: PropTypes.string,
    }).isRequired,
    collegeFair: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      attending: PropTypes.string,
      notes: PropTypes.string,
      cost: PropTypes.string,
      maxColleges: PropTypes.number,
      currentColleges: PropTypes.number,
      emailInstructions: PropTypes.string,
      endTime: PropTypes.string,
      numberOfExpectedStudents: PropTypes.number,
      rsvpDeadline: PropTypes.string,
      startTime: PropTypes.string,
      webInstructions: PropTypes.string,
      autoConfirmRequests: PropTypes.bool,
      hasRsvpDeadlinePassed: PropTypes.bool,
      status: PropTypes.string,
    }).isRequired,
    readOnly: PropTypes.bool,
    hideTitle: PropTypes.bool,
    onRegister: PropTypes.func,
    allowForProfit: PropTypes.bool,
    institutionType: PropTypes.string,
  };

  state = { detailsModalOpen: false };

  toggleDetailsModal = () => { this.setState((prevState) => ({ detailsModalOpen: !prevState.detailsModalOpen })); };

  render() {
    const { schoolId, schoolName, schoolPhone, schoolAddress, primaryContact, collegeFair,
      readOnly, hideTitle, onRegister, allowForProfit, institutionType } = this.props;
    const header = <Header as="h3" styleName="header-strong">{schoolName}</Header>;
    const closeButtonIcon = <Button icon="close" className="close" styleName="close" aria-label="Close" />;
    const disabledRegistration = !onRegister;
    const isForProfitInstitution = institutionType === 'PRIVATE_FORPROFIT';
    const fairAction = getFairAvailabilityAction(collegeFair);
    const detailsAction = fairAction.disabled ? { label: fairAction.label } :
      { label: fairAction.label, onClick: () => { invoke(this, 'props.onRegister', schoolName, collegeFair); this.toggleDetailsModal(); } };

    return (
      <div key={collegeFair.id}>
        {isForProfitInstitution && allowForProfit === false && (
          <div styleName="allow-for-profit-fair-container">
            <Header
              as="h3"
              styleName="allow-for-profit-fair-header"
            >
              This high school only allows non-profit institutions to attend their college fairs.
            </Header>
          </div>
        )}
        <div styleName="header">
          {!hideTitle && (
          <div>
            {readOnly && header}
            {!readOnly && (
            <Modal
              basic
              styleName="details-modal"
              closeIcon={closeButtonIcon}
              trigger={(
                <FreemiumOrHubsSubscriptionSchoolName
                  title={schoolName}
                  onClick={() => this.toggleDetailsModal()}
                />
)}
              onClose={this.toggleDetailsModal}
              open={this.state.detailsModalOpen}
            >
              <HighSchoolDetailDisplay
                contactType="fairs"
                id={schoolId}
                detailsAction={detailsAction}
              >
                <Header as="h2" styleName="details-header">
                  {collegeFair.name}
                </Header>
                <div styleName="details-body" data-cy="fair-availability-details-modal">
                  <FairDetails
                    schoolPhone={schoolPhone}
                    collegeFair={collegeFair}
                    schoolAddress={schoolAddress}
                    primaryContact={primaryContact}
                  />
                </div>
              </HighSchoolDetailDisplay>
            </Modal>
            )} |
            <FreemiumOrHubsSubscriptionFairName title={collegeFair.name} />
          </div>
          )}
          {!readOnly && (
          <div styleName="fair-button">
            <Button
              primary
              type="button"
              size="small"
              floated="right"
              disabled={disabledRegistration || fairAction.disabled || (isForProfitInstitution && allowForProfit === false)}
              onClick={() => { this.props.onRegister(schoolName, collegeFair); }}
            >
              {fairAction.label}
            </Button>
          </div>
          ) }
        </div>
        {!readOnly && (
        <div styleName="details-other">
          <FormattedMessage {...messages.fairRegistrationDeadline} />: <FormattedDate
            value={collegeFair.rsvpDeadline}
            month="long"
            day="numeric"
            year="numeric"
          />
        </div>
        )}
        <FairDetails
          collegeFair={collegeFair}
          schoolPhone={schoolPhone}
          schoolAddress={schoolAddress}
          primaryContact={primaryContact}
        />
      </div>
    );
  }
}

export default injectIntl(FairAvailability);
